/*
  To handle custom client-side JavaScript when an element is added, updated, or removed by a LiveView server, a hook object may be provided via phx-hook.
  Cf [LiveView Docs](https://hexdocs.pm/phoenix_live_view/js-interop.html#client-hooks-via-phx-hook)
*/

import dragula from "dragula";
import PetronilleAiHook from "./petronille_ai_hook.js";
import PetronilleAiOverlayHook from "./petronille_ai_overlay_hook.js";
import PetronilleComposerHook from "./petronille_composer_hook.js";
import PetronilleStageHook from "./petronille_stage_hook.js";
import PetronilleThreadScrollHook from "./petronille_thread_scroll_hook.js";
import TwoWayInputHook from "./two_way_input_hook.js";
import PDFUploaderHook from "./pdf_importer_hook.js";
import PetronilleFileInputHook from "./petronille_file_input_hook.js";
import DatesEditorCalendarHook from "./dates_editor_calendar_hook.js";
import FilestackPickerHook from "./filestack_picker_hook.js";
import LiveChatCockpitHook from "./live_chat_cockpit_hook.js";
import AttendanceSignaturePadHook from "./attendance_signature_pad_hook.js";
import { forget as forgetPopup } from "../framework/components/popup.js";
import { onCloseModalAndInitializePicker } from "../document/upload_document.js";
import { onSelectGivenFolder } from "../document/folder";
import { renderTextDiff } from "../component/text-diff.js";
import { TrackCircleChartUpdates } from "./circle_chart_hook.js";
import { AutomationModalOpener } from "./automation_modal_opener_hook.js";
let Hooks = { TrackCircleChartUpdates, AutomationModalOpener };

/*
Sync a server-driven popup's close back to the LiveComponent.

A popup (see popup.js) emits a native `close` event however it is dismissed:
the cross, Escape, click-outside or a `data-action="popup:close"` button. When
the popup's presence is driven by a server assign (rendered conditionally and
opened via `phx-mounted={JS.dispatch("popup:open")}`), a client-only close
(e.g. the cross) leaves that assign stale, so the element stays mounted and
never re-opens. This hook pushes the event named in `data-close-event` to the
owning component on every close, keeping the server the source of truth.

`data-suspend-close-sync` lets a close pass unreported, for callers that close
and reopen the dialog themselves (see FilestackPicker).
*/
Hooks.SyncPopupCloseToServer = {
  mounted() {
    this.onClose = () => {
      if (this.el.dataset.suspendCloseSync === "true") return;
      this.pushEventTo(this.el, this.el.dataset.closeEvent, {});
    };
    this.el.addEventListener("close", this.onClose);
  },
  destroyed() {
    this.el.removeEventListener("close", this.onClose);
    forgetPopup(this.el);
  },
};

/*
Keep part of the LiveView state in Local Storage
State is consumed at the socket initialization in app.js
*/
Hooks.UseLocalStorage = {
  mounted() {
    this.handleEvent("local-storage-save", ({ key, value }) => {
      const old_params = JSON.parse(localStorage.getItem("liveview-storage") || "{}");
      const old_key_params = old_params[key] || {};
      localStorage.setItem("liveview-storage", JSON.stringify({ ...old_params, [key]: { ...old_key_params, ...value } }));
    });
  },
};

/*
The "Absences Reports" form and its generate button both live in AbsenceReportLive, but the
button is still the shared (Alpine-based) `autocomplet_pdf_button`/`documentButton` component, kept
out of LiveView's patches with `phx-update="ignore"`. Its generation URL is therefore never
re-rendered, and is rebuilt from the `update-generate-url-params` window event instead. This hook
re-dispatches the server-pushed selection as that same window event.

`advice-saved` is relayed the same way: it lets `requestGeneration` (see document_button.js), after
confirming with the user via the `saveAdviceBeforeGeneratePopup` ref and submitting the advice form,
know the save has completed before it proceeds with generation.
*/
Hooks.AbsenceReportGenerateUrlParams = {
  mounted() {
    this.handleEvent("update-generate-url-params", (payload) => {
      window.dispatchEvent(new CustomEvent("update-generate-url-params", { detail: payload }));
    });

    this.handleEvent("advice-saved", () => {
      window.dispatchEvent(new CustomEvent("advice-saved"));
    });
  },
};

/*
Listen and send selected card events for advancement liveview
*/
Hooks.KanbanCardSelection = {
  mounted() {
    document.addEventListener("kanban-selected-card", (e) => {
      const card = e.detail.args.card;

      if (card && card.alert_advancement != 0) {
        this.pushEvent("kanban-select-card", { training_session_id: card.id });
      } else {
        this.pushEvent("kanban-unselect-card", {});
      }
    });
  },
};

/*
Scroll to a given id
*/
Hooks.GoToId = {
  mounted() {
    this.handleEvent("go-to-tasks", () => {
      document.querySelector("#task-section").scrollIntoView({ behavior: "smooth" });
    });

    window.addEventListener("go-to-my-tasks", () => {
      document.getElementById("goto-task").scrollIntoView();
    });
  },
};

/*
Set up liveview drag and drop containers.
The content from all component with the attribute `drag-drop-container` can be dragged and dropped.
The callback pushes the id of the moved element and the new order of the elements of the containers to modify the position of items in the list.
The mecanism can be further customized by providing :
- handles : the part of the element that can be used for dragging
- groups : to block dropping element of a group in a droppable zone of another group
- data : to pass data to the drop event handler
*/
Hooks.UseDragDrop = {
  mounted() {
    let containers = Array.from(document.querySelectorAll("*[drag-drop-container]"));
    this.drake = dragula(containers, {
      mirrorContainer: this.el,
      direction: "vertical",
      moves: function (_el, _source, handle, _sibling) {
        document.activeElement?.blur();
        return handle.closest("[drag-drop-handle]");
      },
      accepts: function (el, target, _source, _sibling) {
        if (target.hasAttribute("drag-drop-group"))
          return target.getAttribute("drag-drop-group") === el.getAttribute("drag-drop-group");
        return true;
      },
    });
    this.drake.on("drop", (e, target) => {
      this.pushEvent("drop", {
        containerData: JSON.parse(target.getAttribute("drag-drop-data") || "{}"),
        moved: e.getAttribute("drag-drop-id"),
        order: Array.from(target.querySelectorAll("[drag-drop-id]")).map((it) => it.getAttribute("drag-drop-id")),
      });
    });
  },
  updated() {
    this.drake.containers = Array.from(document.querySelectorAll("*[drag-drop-container]"));
  },
};

/*
Accordion in liveview are tricky because any update of the component will re-render the accordion and its state (open/close) will not be kept.
Often it will also be demanded as an UX improvement to keep the state of the accordion between page refreshed.
This hook take care of both feature by storing the id of opened accordions in the local storage.
*/

const liveviewAccordionKey = "liveview-accordion";

const toggleAccordionStateInStorage = (e) => {
  const id = e.target.id;
  const open = e.target.open;

  const openedAccordions = JSON.parse(localStorage.getItem(liveviewAccordionKey) || "[]");
  const index = openedAccordions.indexOf(id);

  if (open && index !== -1) return;
  if (!open && index == -1) return;

  if (open && index == -1) {
    openedAccordions.push(id);
  } else {
    openedAccordions.splice(index, 1);
  }

  localStorage.setItem(liveviewAccordionKey, JSON.stringify(openedAccordions));
};

const maybeOpenAccordion = (elt) => {
  const id = elt.getAttribute("id");
  const openedAccordions = JSON.parse(localStorage.getItem(liveviewAccordionKey) || "[]");
  const index = openedAccordions.indexOf(id);

  if (index !== -1) elt.setAttribute("open", true);
};

/*
Opt-in, via `data-toggle-event`: report the open state to the LiveView too.

Without it the server keeps re-rendering a stale `open` attribute, and every patch has morphdom
strip it off an accordion the user had opened (`mergeAttrs` removes any attribute missing from the
server render). The document shrinks, the browser clamps `scrollTop`, then `updated()` re-opens the
accordion below - leaving the page scrolled to a position that no longer shows what was being read.
Handing the state over on `toggle` keeps the two in sync, so `open` never moves during a patch.
*/
const pushAccordionState = function (el) {
  if (el.dataset.toggleEvent) {
    this.pushEvent(el.dataset.toggleEvent, { id: el.id, open: el.open });
  }
};

Hooks.UseAccordionLocaleStorageState = {
  mounted() {
    this.el.addEventListener("toggle", (e) => {
      toggleAccordionStateInStorage(e);
      pushAccordionState.call(this, this.el);
    });
    maybeOpenAccordion(this.el);
  },
  updated() {
    maybeOpenAccordion(this.el);
  },
};

Hooks.RetriggerDocumentsFilter = {
  mounted() {
    window.addEventListener(`phx:rerender_documents`, (e) => {
      const folderId = e.detail.id;
      const selectedFolder = folderId
        ? document.querySelector(`.documents__folders-block[data-folder-id="${folderId}"]`)
        : document.querySelector(`.documents__folders-block,.root-folder`);

      const mockedEvent = {
        currentTarget: selectedFolder,
      };
      onSelectGivenFolder(mockedEvent);
    });
  },
};

Hooks.CreateDocuments = {
  mounted() {
    this.el.addEventListener("click", (event) => {
      onCloseModalAndInitializePicker(event).then((result) => {
        this.pushEvent("documents_add", result);
      });
    });
  },
};

Hooks.ExportFundae = {
  mounted() {
    window.addEventListener(`phx:download-export-fundae-file`, (event) => {
      const a = document.createElement("a");
      a.href = event.detail.url;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
  },
};

Hooks.ScrollToBottom = {
  mounted() {
    this.scrollToLastChild();
  },
  updated() {
    this.scrollToLastChild();
  },
  scrollToLastChild() {
    const lastChild = this.el.lastElementChild;
    if (!lastChild) return;
    lastChild.scrollIntoView();
  },
};

Hooks.PetronilleAiHook = PetronilleAiHook;
Hooks.PetronilleAiOverlayHook = PetronilleAiOverlayHook;
Hooks.PetronilleComposerHook = PetronilleComposerHook;
Hooks.PetronilleStageHook = PetronilleStageHook;
Hooks.PetronilleThreadScrollHook = PetronilleThreadScrollHook;

Hooks.TwoWayInputHook = TwoWayInputHook;

Hooks.PDFUploaderHook = PDFUploaderHook;
Hooks.PetronilleFileInputHook = PetronilleFileInputHook;

Hooks.TextDiff = {
  mounted() {
    const before = this.el.dataset.before || "";
    const after = this.el.dataset.after || "";
    renderTextDiff(this.el, before, after);
  },
  updated() {
    const before = this.el.dataset.before || "";
    const after = this.el.dataset.after || "";
    renderTextDiff(this.el, before, after);
  },
};

Hooks.DatesEditorCalendar = DatesEditorCalendarHook;

Hooks.LiveChatCockpit = LiveChatCockpitHook;

Hooks.AttendanceSignaturePad = AttendanceSignaturePadHook;
Hooks.FilestackPicker = FilestackPickerHook;

Hooks.SubmitOnCmdEnter = {
  mounted() {
    const form = this.el;

    const SubmitOnCmdEnter = (e) => {
      if (e.metaKey && e.key === "Enter") {
        form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
      }
    };

    form.addEventListener("keydown", SubmitOnCmdEnter);
  },
};

/*
Scroll the element into view when it mounts. Used to reveal a deep-linked element that only appears
after an async render (e.g. an accordion the server opened from a query param).
*/
Hooks.ScrollIntoView = {
  mounted() {
    this.el.scrollIntoView({ behavior: "smooth", block: "center" });
  },
};

export { Hooks };
